import React, { useEffect, useMemo, useState } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import moment from 'moment'

import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import RootState from '../../core/root-state'
import {
  foodPriceHistoryActions,
  FoodPriceHistory,
  FoodPriceHistoryParam,
  FoodPriceHistoryPagination,
} from '../../core/food-price-history'
import { foodsActions, Food } from '../../core/foods'
import {
  Brand,
  brandsActions,
  getDefaultBrandCodeByRole,
  BrandCode,
} from '../../core/brands'
import { roleType, Permission, isSbjUser } from '../../core/auth0'
import withPermissionCheck from '../../core/access-control/with-permission-check'
import FoodPriceHistoryFiltersComponent, {
  FoodPriceHistoryFilterForm,
} from '../components/food-price-history/food-price-history-filters'
import FoodPriceHistoryTable from '../components/food-price-history/food-price-history-table'
import Pagination from '../components/pagination/pagination'
import styles from '../styles/modules/food-price-history/food-price-history.module.scss'

interface StateProps {
  histories: FoodPriceHistory[]
  pagination: FoodPriceHistoryPagination
  isLoading: boolean
  foods: Food[]
  brands: Brand[]
  role: roleType
}

interface DispatchProps {
  fetchFoodPriceHistories: (params?: Partial<FoodPriceHistoryParam>) => void
  fetchFoods: (brandCode?: string | null) => void
  fetchBrands: () => void
}

type Props = StateProps & DispatchProps

const formatDate = (value: Date | null) =>
  value ? moment(value).format('YYYY-MM-DD') : null

const FoodPriceHistoryScreen = ({
  histories,
  pagination,
  isLoading,
  foods,
  brands,
  role,
  fetchFoodPriceHistories,
  fetchFoods,
  fetchBrands,
}: Props) => {
  const isAdmin = role === roleType.ADMIN
  const defaultBrandCode = useMemo(
    () => getDefaultBrandCodeByRole(isSbjUser(role)) || '',
    [role]
  )

  const [formState, setFormState] = useState<FoodPriceHistoryFilterForm>({
    foodId: '',
    dateFrom: null,
    dateTo: null,
    brandCode: defaultBrandCode || '',
  })

  useEffect(() => {
    if (isAdmin) {
      fetchBrands()
    }
  }, [isAdmin, fetchBrands])

  useEffect(() => {
    const initialBrand = isAdmin
      ? formState.brandCode || defaultBrandCode
      : defaultBrandCode

    fetchFoods(initialBrand || null)
    fetchFoodPriceHistories({
      brandCode: initialBrand || null,
      page: 1,
      per: 20,
    })
    setFormState(prev => ({
      ...prev,
      brandCode: initialBrand || '',
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = (field: 'foodId' | 'brandCode', value: string) => {
    setFormState(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleFoodChange = (foodId: string) => {
    setFormState(prev => ({
      ...prev,
      foodId,
    }))
    // 選択と同時にAPIリクエストを発行
    const params: Partial<FoodPriceHistoryParam> = {
      foodId: foodId ? Number(foodId) : null,
      dateFrom: formatDate(formState.dateFrom),
      dateTo: formatDate(formState.dateTo),
      brandCode: isAdmin
        ? formState.brandCode || null
        : defaultBrandCode || null,
      page: 1,
      per: pagination.per || 20,
    }
    fetchFoodPriceHistories(params)
  }

  const handleBrandChange = (brandCode: BrandCode | '') => {
    fetchFoods(brandCode || null)
    setFormState(prev => ({
      ...prev,
      foodId: '',
      brandCode,
    }))
    // ブランド変更時も即座にAPIリクエストを発行
    const params: Partial<FoodPriceHistoryParam> = {
      foodId: null,
      dateFrom: formatDate(formState.dateFrom),
      dateTo: formatDate(formState.dateTo),
      brandCode: brandCode || null,
      page: 1,
      per: pagination.per || 20,
    }
    fetchFoodPriceHistories(params)
  }

  const buildParams = (override?: Partial<FoodPriceHistoryParam>) => {
    const params: Partial<FoodPriceHistoryParam> = {
      foodId: formState.foodId ? Number(formState.foodId) : null,
      dateFrom: formatDate(formState.dateFrom),
      dateTo: formatDate(formState.dateTo),
      brandCode: isAdmin
        ? formState.brandCode || null
        : defaultBrandCode || null,
      page: 1,
      per: pagination.per || 20,
    }
    return {
      ...params,
      ...override,
    }
  }

  const handleDateFromChange = (date: Date | null) => {
    if (!date) {
      setFormState(prev => ({
        ...prev,
        dateFrom: null,
      }))
      // 日付クリア時も即座にAPIリクエストを発行
      const params: Partial<FoodPriceHistoryParam> = {
        foodId: formState.foodId ? Number(formState.foodId) : null,
        dateFrom: null,
        dateTo: formatDate(formState.dateTo),
        brandCode: isAdmin
          ? formState.brandCode || null
          : defaultBrandCode || null,
        page: 1,
        per: pagination.per || 20,
      }
      fetchFoodPriceHistories(params)
      return
    }

    const today = new Date()
    let nextStartDate = date > today ? today : date

    if (formState.dateTo && nextStartDate > formState.dateTo) {
      nextStartDate = formState.dateTo
    }

    setFormState(prev => ({
      ...prev,
      dateFrom: nextStartDate,
    }))

    // 日付変更時も即座にAPIリクエストを発行
    const params: Partial<FoodPriceHistoryParam> = {
      foodId: formState.foodId ? Number(formState.foodId) : null,
      dateFrom: formatDate(nextStartDate),
      dateTo: formatDate(formState.dateTo),
      brandCode: isAdmin
        ? formState.brandCode || null
        : defaultBrandCode || null,
      page: 1,
      per: pagination.per || 20,
    }
    fetchFoodPriceHistories(params)
  }

  const handleDateToChange = (date: Date | null) => {
    let newDateTo: Date | null = null
    let newDateFrom: Date | null = formState.dateFrom

    if (date > formState.dateFrom) {
      const now = new Date()
      if (date < now) {
        newDateTo = date
      } else {
        newDateTo = now
      }
      setFormState(prev => ({
        ...prev,
        dateTo: newDateTo,
      }))
    } else {
      newDateFrom = date
      newDateTo = date
      setFormState(prev => ({
        ...prev,
        dateFrom: date,
        dateTo: date,
      }))
    }

    // 日付変更時も即座にAPIリクエストを発行
    const params: Partial<FoodPriceHistoryParam> = {
      foodId: formState.foodId ? Number(formState.foodId) : null,
      dateFrom: formatDate(newDateFrom),
      dateTo: formatDate(newDateTo),
      brandCode: isAdmin
        ? formState.brandCode || null
        : defaultBrandCode || null,
      page: 1,
      per: pagination.per || 20,
    }
    fetchFoodPriceHistories(params)
  }

  const handleClear = () => {
    // フォームをクリア（ブランドは維持）
    setFormState({
      foodId: '',
      dateFrom: null,
      dateTo: null,
      brandCode: formState.brandCode,
    })
    // クリア後の初期データ取得
    const params: Partial<FoodPriceHistoryParam> = {
      foodId: null,
      dateFrom: null,
      dateTo: null,
      brandCode: isAdmin
        ? formState.brandCode || null
        : defaultBrandCode || null,
      page: 1,
      per: pagination.per || 20,
    }
    fetchFoodPriceHistories(params)
  }

  const handlePageChange = (page: number) => {
    fetchFoodPriceHistories(buildParams({ page }))
  }

  return (
    <Layout location={'food_price_history'}>
      <Wrapper title="フード価格変更履歴">
        <div className={styles.container}>
          <FoodPriceHistoryFiltersComponent
            formState={formState}
            onChange={handleChange}
            onFoodChange={handleFoodChange}
            onClear={handleClear}
            onBrandChange={handleBrandChange}
            onChangeDateFrom={handleDateFromChange}
            onChangeDateTo={handleDateToChange}
            foods={foods}
            brands={brands}
            isAdmin={isAdmin}
            isLoading={isLoading}
          />
          <FoodPriceHistoryTable histories={histories} isAdmin={isAdmin} />
          <div className={styles.pagination}>
            <Pagination
              currentPage={pagination.page || 1}
              totalCount={pagination.total}
              pageSize={pagination.per}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </Wrapper>
    </Layout>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  histories: state.foodPriceHistory.histories,
  pagination: state.foodPriceHistory.pagination,
  isLoading: state.foodPriceHistory.isLoading,
  foods: state.foods.foods,
  brands: state.brands.brands,
  role: state.auth0.role,
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  fetchFoodPriceHistories: (params?: FoodPriceHistoryParam) =>
    dispatch(foodPriceHistoryActions.fetchFoodPriceHistories(params)),
  fetchFoods: (brandCode?: string | null) =>
    dispatch(foodsActions.fetchFoods(brandCode)),
  fetchBrands: () => dispatch(brandsActions.fetchBrands()),
})

const ConnectedFoodPriceHistoryScreen = connect(
  mapStateToProps,
  mapDispatchToProps
)(FoodPriceHistoryScreen)

export default withPermissionCheck(ConnectedFoodPriceHistoryScreen, {
  requiredPermission: Permission.ITEM_READ,
  redirectTo: '/404',
})
